import React, { useState, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import Tooltip from '@mui/material/Tooltip';
import { useAnalytics } from '../../../context/AnalyticsContext';
import './RuleConfigModal.css'; // Reuse the same styles

const TIMEFRAMES = [
  { value: 'M1', label: '1 Minute' },
  { value: 'M5', label: '5 Minutes' },
  { value: 'M15', label: '15 Minutes' },
  { value: 'M30', label: '30 Minutes' },
  { value: 'H1', label: '1 Hour' },
  { value: 'H4', label: '4 Hours' },
  { value: 'D', label: 'Daily' },
  { value: 'W', label: 'Weekly' },
];

// Available AI models for analytics
const AI_MODELS = [
  { value: 'gpt-5-nano', label: 'GPT-5 Nano (Fast)', description: 'Fastest, good for real-time analysis' },
  { value: 'gpt-5-mini', label: 'GPT-5 Mini', description: 'Balanced speed and quality' },
  { value: 'gpt-5', label: 'GPT-5', description: 'Highest quality analysis' },
];

// Timeframes available for multi-timeframe analysis
const MTF_TIMEFRAMES = [
  { value: 'M5', label: 'M5' },
  { value: 'M15', label: 'M15' },
  { value: 'M30', label: 'M30' },
  { value: 'H1', label: 'H1' },
  { value: 'H4', label: 'H4' },
  { value: 'D', label: 'Daily' },
];

const CATEGORY_ICONS = {
  MOMENTUM: '\u26A1',
  TREND: '\u2197',
  VOLUME: '\u2593',
  VOLATILITY: '\u223F',
  PATTERN: '\u25B2',
  AI: '\u2728',
};

const ModuleConfigModal = () => {
  const {
    moduleConfigModalOpen,
    selectedModuleForConfig,
    closeModuleConfigModal,
    updateModule,
    removeModule,
  } = useAnalytics();

  const [localConfig, setLocalConfig] = useState({});
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [toggling, setToggling] = useState(false);

  // Initialize from current config when modal opens
  useEffect(() => {
    if (selectedModuleForConfig) {
      const { pipelineModule, libraryModule } = selectedModuleForConfig;

      // Parse default config from library module
      let defaultConfig = {};
      if (libraryModule?.defaultConfigJson) {
        try {
          defaultConfig = typeof libraryModule.defaultConfigJson === 'string'
            ? JSON.parse(libraryModule.defaultConfigJson)
            : libraryModule.defaultConfigJson;
        } catch (e) {
          // Ignore parse errors
        }
      }

      // Parse custom config from pipeline module
      let customConfig = {};
      if (pipelineModule?.customConfigJson) {
        try {
          customConfig = typeof pipelineModule.customConfigJson === 'string'
            ? JSON.parse(pipelineModule.customConfigJson)
            : pipelineModule.customConfigJson;
        } catch (e) {
          // Ignore parse errors
        }
      }

      // Merge configs, custom overrides default
      const mergedConfig = {
        ...defaultConfig,
        ...customConfig,
        timeframe: customConfig.timeframe || defaultConfig.timeframe || libraryModule?.defaultTimeframe || 'H1',
      };

      setLocalConfig(mergedConfig);
      setHasChanges(false);
    }
  }, [selectedModuleForConfig]);

  const handleConfigChange = (key, value) => {
    setLocalConfig(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!selectedModuleForConfig) return;

    setSaving(true);
    await updateModule(selectedModuleForConfig.pipelineModule.id, {
      customConfigJson: JSON.stringify(localConfig),
    });
    setSaving(false);
    closeModuleConfigModal();
  };

  const handleToggleEnabled = async () => {
    if (!selectedModuleForConfig) return;

    setToggling(true);
    const currentEnabled = selectedModuleForConfig.pipelineModule.enabled !== false;
    await updateModule(selectedModuleForConfig.pipelineModule.id, {
      enabled: !currentEnabled,
    });
    // Update local reference
    selectedModuleForConfig.pipelineModule.enabled = !currentEnabled;
    setToggling(false);
  };

  const handleDelete = async () => {
    if (!selectedModuleForConfig) return;
    await removeModule(selectedModuleForConfig.pipelineModule.id);
    closeModuleConfigModal();
  };

  if (!moduleConfigModalOpen || !selectedModuleForConfig) return null;

  const { pipelineModule, libraryModule } = selectedModuleForConfig;
  const isEnabled = pipelineModule?.enabled !== false;
  const moduleName = libraryModule?.name || pipelineModule?.libraryModuleId || 'Module';
  const moduleDescription = libraryModule?.description || '';
  const moduleCategory = libraryModule?.category || 'General';

  return (
    <div className="rule-config-modal-overlay" onClick={closeModuleConfigModal}>
      <div className="rule-config-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Configure Module</h2>
          <div className="modal-header-actions">
            <Tooltip title="Remove module" arrow>
              <button className="modal-delete-btn" onClick={handleDelete}>
                <DeleteOutlineIcon fontSize="small" />
              </button>
            </Tooltip>
            <button className="modal-close-btn" onClick={closeModuleConfigModal}>
              <CloseIcon />
            </button>
          </div>
        </div>

        <div className="modal-content">
          {/* Module Name */}
          <div className="form-field">
            <label className="form-label">Module Name</label>
            <input
              type="text"
              value={moduleName}
              className="form-input"
              disabled
            />
          </div>

          {/* Description */}
          {moduleDescription && (
            <div className="form-field">
              <label className="form-label">Description</label>
              <textarea
                value={moduleDescription}
                className="form-textarea"
                rows={2}
                disabled
              />
            </div>
          )}

          <div className="rule-meta">
            <span className="rule-category">
              {CATEGORY_ICONS[moduleCategory] || '\u2022'} {moduleCategory}
            </span>
          </div>

          {/* Enable/Disable Toggle */}
          <div className="rule-status-section">
            <div className="status-label">Status</div>
            <button
              className={`status-toggle ${isEnabled ? 'enabled' : 'disabled'}`}
              onClick={handleToggleEnabled}
              disabled={toggling}
            >
              {isEnabled ? (
                <>
                  <ToggleOnIcon className="toggle-icon" />
                  <span>Enabled</span>
                </>
              ) : (
                <>
                  <ToggleOffIcon className="toggle-icon" />
                  <span>Disabled</span>
                </>
              )}
            </button>
          </div>

          <div className="config-divider" />

          {/* Timeframe Configuration - not for AI modules which have multi-timeframe selector */}
          {moduleCategory !== 'AI' && (
            <div className="form-field">
              <label className="form-label">
                Timeframe
                <span className="form-label-hint">Analysis timeframe for this module</span>
              </label>
              <select
                className="form-input form-select"
                value={localConfig.timeframe || 'H1'}
                onChange={(e) => handleConfigChange('timeframe', e.target.value)}
              >
                {TIMEFRAMES.map(tf => (
                  <option key={tf.value} value={tf.value}>{tf.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Weight Configuration */}
          <div className="form-field">
            <label className="form-label">
              Weight
              <span className="form-label-hint">Influence on final analysis result (0.1 - 2.0)</span>
            </label>
            <input
              type="number"
              inputMode="decimal"
              className="form-input"
              value={localConfig.weight ?? pipelineModule?.weight ?? 1.0}
              onChange={(e) => handleConfigChange('weight', parseFloat(e.target.value) || 1.0)}
              min="0.1"
              max="2.0"
              step="0.1"
            />
          </div>

          {/* Module-specific parameters based on category */}
          {moduleCategory === 'MOMENTUM' && (
            <>
              <div className="form-field">
                <label className="form-label">
                  Period
                  <span className="form-label-hint">Lookback period for calculation</span>
                </label>
                <input
                  type="number"
                  inputMode="decimal"
                  className="form-input"
                  value={localConfig.period ?? 14}
                  onChange={(e) => handleConfigChange('period', parseInt(e.target.value) || 14)}
                  min="2"
                  max="200"
                />
              </div>
              <div className="form-field">
                <label className="form-label">
                  Overbought Level
                  <span className="form-label-hint">Upper threshold for signals</span>
                </label>
                <input
                  type="number"
                  inputMode="decimal"
                  className="form-input"
                  value={localConfig.overbought ?? 70}
                  onChange={(e) => handleConfigChange('overbought', parseInt(e.target.value) || 70)}
                  min="50"
                  max="100"
                />
              </div>
              <div className="form-field">
                <label className="form-label">
                  Oversold Level
                  <span className="form-label-hint">Lower threshold for signals</span>
                </label>
                <input
                  type="number"
                  inputMode="decimal"
                  className="form-input"
                  value={localConfig.oversold ?? 30}
                  onChange={(e) => handleConfigChange('oversold', parseInt(e.target.value) || 30)}
                  min="0"
                  max="50"
                />
              </div>
            </>
          )}

          {moduleCategory === 'TREND' && (
            <>
              <div className="form-field">
                <label className="form-label">
                  Fast Period
                  <span className="form-label-hint">Short-term moving average period</span>
                </label>
                <input
                  type="number"
                  inputMode="decimal"
                  className="form-input"
                  value={localConfig.fastPeriod ?? 12}
                  onChange={(e) => handleConfigChange('fastPeriod', parseInt(e.target.value) || 12)}
                  min="2"
                  max="100"
                />
              </div>
              <div className="form-field">
                <label className="form-label">
                  Slow Period
                  <span className="form-label-hint">Long-term moving average period</span>
                </label>
                <input
                  type="number"
                  inputMode="decimal"
                  className="form-input"
                  value={localConfig.slowPeriod ?? 26}
                  onChange={(e) => handleConfigChange('slowPeriod', parseInt(e.target.value) || 26)}
                  min="5"
                  max="200"
                />
              </div>
            </>
          )}

          {moduleCategory === 'VOLATILITY' && (
            <>
              <div className="form-field">
                <label className="form-label">
                  Period
                  <span className="form-label-hint">Volatility calculation period</span>
                </label>
                <input
                  type="number"
                  inputMode="decimal"
                  className="form-input"
                  value={localConfig.period ?? 14}
                  onChange={(e) => handleConfigChange('period', parseInt(e.target.value) || 14)}
                  min="2"
                  max="100"
                />
              </div>
              <div className="form-field">
                <label className="form-label">
                  Multiplier
                  <span className="form-label-hint">Standard deviation multiplier</span>
                </label>
                <input
                  type="number"
                  inputMode="decimal"
                  className="form-input"
                  value={localConfig.multiplier ?? 2.0}
                  onChange={(e) => handleConfigChange('multiplier', parseFloat(e.target.value) || 2.0)}
                  min="0.5"
                  max="5.0"
                  step="0.1"
                />
              </div>
            </>
          )}

          {moduleCategory === 'AI' && (
            <>
              <div className="form-field">
                <label className="form-label">
                  AI Model
                  <span className="form-label-hint">Model used for analysis</span>
                </label>
                <select
                  className="form-input form-select"
                  value={localConfig.model || 'gpt-5-nano'}
                  onChange={(e) => handleConfigChange('model', e.target.value)}
                >
                  {AI_MODELS.map(model => (
                    <option key={model.value} value={model.value} title={model.description}>
                      {model.label}
                    </option>
                  ))}
                </select>
                <span className="form-field-description">
                  {AI_MODELS.find(m => m.value === (localConfig.model || 'gpt-5-nano'))?.description}
                </span>
              </div>

              <div className="form-field">
                <label className="form-label">
                  Analysis Timeframes
                  <span className="form-label-hint">Select 2-4 timeframes for confluence analysis</span>
                </label>
                <div className="timeframe-chips">
                  {MTF_TIMEFRAMES.map(tf => {
                    const timeframes = localConfig.timeframes || ['M30', 'H1', 'H4'];
                    const isSelected = timeframes.includes(tf.value);
                    return (
                      <button
                        key={tf.value}
                        type="button"
                        className={`timeframe-chip ${isSelected ? 'selected' : ''}`}
                        onClick={() => {
                          const current = localConfig.timeframes || ['M30', 'H1', 'H4'];
                          let newTimeframes;
                          if (isSelected) {
                            // Don't allow less than 2 timeframes
                            if (current.length <= 2) return;
                            newTimeframes = current.filter(t => t !== tf.value);
                          } else {
                            // Don't allow more than 4 timeframes
                            if (current.length >= 4) return;
                            newTimeframes = [...current, tf.value];
                          }
                          // Sort by timeframe order
                          const order = MTF_TIMEFRAMES.map(t => t.value);
                          newTimeframes.sort((a, b) => order.indexOf(a) - order.indexOf(b));
                          handleConfigChange('timeframes', newTimeframes);
                        }}
                      >
                        {tf.label}
                      </button>
                    );
                  })}
                </div>
                <span className="form-field-description">
                  Selected: {(localConfig.timeframes || ['M30', 'H1', 'H4']).join(' → ')}
                </span>
              </div>
            </>
          )}

        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={closeModuleConfigModal}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={saving || !hasChanges}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModuleConfigModal;
